import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';
import type {
  TeamMember,
  NotificationPreferences,
  EscalationContact,
  EscalationContactUpsert,
  Integration,
  AvailableIntegration,
  WebhookEndpoint,
  WebhookDelivery,
  BillingInfo,
} from '@/api/types/settings.types';

export function useProfile() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['profile', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/auth/me');
      return data.data;
    },
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
  });
}

export function useUpdateProfile() {
  const { tenantId, updateUser } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { displayName?: string; firstName?: string; lastName?: string }) => {
      const { data } = await apiClient.patch('/auth/me', body);
      return data.data;
    },
    // Update the auth store from the request VARIABLES (camelCase) so Header + Sidebar,
    // which read useAuthStore().user.displayName, refresh without a full reload.
    // ⚠ Not the response — PATCH /auth/me returns snake_case (display_name); using it would
    // write `undefined`. displayName is the only store-carried name field. B-PROFILE-HEADER-STALE-AFTER-SAVE-1.
    onSuccess: (_data, variables) => {
      if (variables.displayName !== undefined) {
        updateUser({ displayName: variables.displayName });
      }
      qc.invalidateQueries({ queryKey: ['profile', tenantId] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (body: { currentPassword: string; newPassword: string }) => {
      const { data } = await apiClient.patch('/auth/password', body);
      return data;
    },
  });
}

export interface MfaSetupData {
  secret: string;
  otpauthUrl: string;
}

export function useMfaSetup() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.get<{ data: MfaSetupData }>('/auth/mfa/setup');
      return data.data;
    },
  });
}

export function useVerifyMfa() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) => {
      const { data } = await apiClient.post('/auth/mfa/verify', { code });
      return data.data as { message: string };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['profile', tenantId] }),
  });
}

export function useResetSelfMfa() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (password: string) => {
      const { data } = await apiClient.post('/auth/mfa/reset-self', { password });
      return data.data as { message: string };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['profile', tenantId] }),
  });
}

export function useTeamMembers() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['team-members', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/users');
      return data.data as TeamMember[];
    },
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
  });
}

// USER_ADMIN RBAC cluster — what the signed-in user may do on the user-management surface, read LIVE from the server
// (from the database, like the server's own gates — not the token's roles), and every tenant role marked assignable or
// not for this user. The UI offers exactly what the server accepts; the server still decides every request.
export interface TenantRoleOption {
  roleId: string;
  name: string;
  displayName: string;
  description: string;
  privileged: boolean;
  assignable: boolean;
}
export interface UserAdminActor {
  canAdministerUsers: boolean;
  isSuperAdmin: boolean;
  roles: TenantRoleOption[];
}

export function useUserAdminActor() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['user-admin-actor', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/users/assignable-roles');
      return data.data as UserAdminActor;
    },
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

// Small-items batch commit 5 — read-only reference to the org's Primary Admin (the oldest
// active user, mirroring the admin-portal card). Read-only; changing it goes through Support.
export interface PrimaryAdminRef {
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  status: string;
}

export function usePrimaryAdmin() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['primary-admin', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/team/primary-admin');
      return data.data as PrimaryAdminRef | null;
    },
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
  });
}

export function useInviteTeamMember() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { email: string; firstName?: string; lastName?: string; role_ids: string[] }) => {
      const { data } = await apiClient.post('/auth/invite', body);
      return data.data as { invitationUrl: string };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-members', tenantId] }),
  });
}

export function useUpdateTeamMember() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string; display_name?: string; first_name?: string; last_name?: string }) => {
      const { data } = await apiClient.patch(`/team/members/${id}`, body);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-members', tenantId] }),
  });
}

export function useChangeTeamMemberRole() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, role }: { id: string; role: string }) => {
      const { data } = await apiClient.patch(`/team/members/${id}/role`, { role });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-members', tenantId] }),
  });
}

export function useUpdateMemberRoles() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, add, remove }: { id: string; add: string[]; remove: string[] }) => {
      const { data } = await apiClient.patch<{ data: { roleIds: string[] } }>(`/users/${id}/roles`, { add, remove });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-members', tenantId] });
      qc.invalidateQueries({ queryKey: ['user-admin-actor', tenantId] });
    },
  });
}

export function useResetTeamMemberPassword() {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post(`/team/members/${id}/reset-password`);
      return data.data;
    },
  });
}

export function useResetTeamMemberMfa() {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post(`/team/members/${id}/reset-mfa`);
      return data.data;
    },
  });
}

export function useResendTeamMemberInvitation() {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post(`/team/members/${id}/resend-invitation`);
      return data.data;
    },
  });
}

export function useDeleteTeamMember() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete(`/team/members/${id}`);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-members', tenantId] }),
  });
}

export function useNotificationPrefs() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['notification-prefs', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/settings/notification-preferences');
      return data.data as NotificationPreferences;
    },
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
  });
}

export function useUpdateNotificationPrefs() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Partial<NotificationPreferences>) => {
      // Backend schema uses camelCase; translate from the snake_case NotificationPreferences shape
      const payload: Record<string, unknown> = {};
      if (body.critical_alerts_dl       !== undefined) payload.criticalAlertsDl        = body.critical_alerts_dl;
      if (body.high_alerts_dl            !== undefined) payload.highAlertsDl            = body.high_alerts_dl;
      if (body.general_updates_dl        !== undefined) payload.generalUpdatesDl        = body.general_updates_dl;
      if (body.product_updates_dl        !== undefined) payload.productUpdatesDl        = body.product_updates_dl;
      if (body.digest_enabled            !== undefined) payload.digestEnabled           = body.digest_enabled;
      if (body.digest_frequency          !== undefined) payload.digestFrequency         = body.digest_frequency;
      if (body.digest_time               !== undefined) payload.digestTimeUtc           = body.digest_time;
      if (body.assessment_notifications  !== undefined) payload.assessmentNotifications = body.assessment_notifications;
      if (body.security_notifications    !== undefined) payload.securityNotifications   = body.security_notifications;
      if (body.compliance_notifications  !== undefined) payload.complianceNotifications = body.compliance_notifications;
      if (body.product_update_notifications !== undefined) payload.productNotifications = body.product_update_notifications;
      const { data } = await apiClient.patch('/settings/notification-preferences', payload);
      return data.data as NotificationPreferences;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notification-prefs', tenantId] }),
  });
}

export function useEscalationContacts() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['escalation-contacts', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/settings/escalation-contacts');
      return data.data as EscalationContact[];
    },
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
  });
}

export function useUpsertEscalationContact() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: EscalationContactUpsert) => {
      const method = body.id ? 'patch' : 'post';
      const url = body.id
        ? `/settings/escalation-contacts/${body.id}`
        : '/settings/escalation-contacts';
      const { data } = await apiClient[method](url, body);
      return data.data as EscalationContact;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['escalation-contacts', tenantId] }),
  });
}

// ── S5b: Support Verification Code ──────────────────────────────────────────────

export interface SupportVerificationConfig { generator_roles: string[]; }
export type SupportCallReason = 'billing' | 'account_access' | 'compliance' | 'vendor_assessment' | 'other';

// Generate an on-the-fly support code (returned once). Not cached — the caller
// displays it immediately and it is never persisted client-side.
export function useGenerateSupportCode() {
  return useMutation({
    mutationFn: async (body: { confirmed: true; call_reason: SupportCallReason }) => {
      const { data } = await apiClient.post('/settings/support-verification-code', body);
      return data.data as { code: string; expires_at: string };
    },
  });
}

export function useSupportVerificationConfig() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['support-verification-config', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/settings/support-verification-config');
      return data.data as SupportVerificationConfig;
    },
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
  });
}

export function useUpdateSupportVerificationConfig() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (generator_roles: string[]) => {
      const { data } = await apiClient.patch('/settings/support-verification-config', { generator_roles });
      return data.data as SupportVerificationConfig;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support-verification-config', tenantId] }),
  });
}

export function useAvailableIntegrations() {
  return useQuery({
    queryKey: ['available-integrations'],
    queryFn: async () => {
      const { data } = await apiClient.get('/integrations/available');
      return data.data as AvailableIntegration[];
    },
    staleTime: 60 * 60_000,
  });
}

export function useIntegrations() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['integrations', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/integrations');
      return data.data as Integration[];
    },
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
  });
}

export function useCreateIntegration() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { integrationType: string; displayName: string; config: Record<string, string> }) => {
      const { data } = await apiClient.post('/integrations', {
        integration_type: body.integrationType,
        display_name: body.displayName,
        config: body.config,
      });
      return data.data as Integration;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', tenantId] }),
  });
}

export function useUpdateIntegration() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { id: string; config: Record<string, string> }) => {
      const { data } = await apiClient.patch(`/integrations/${body.id}`, { config: body.config });
      return data.data as Integration;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', tenantId] }),
  });
}

export function useDeleteIntegration() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/integrations/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', tenantId] }),
  });
}

export function useTestIntegration() {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post(`/integrations/${id}/test`);
      return data.data as { success: boolean; message: string };
    },
  });
}

export function useWebhooks() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['webhooks', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/webhooks');
      return data.data as WebhookEndpoint[];
    },
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
  });
}

export function useWebhookDeliveries(webhookId: string) {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['webhook-deliveries', tenantId, webhookId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/webhooks/${webhookId}/deliveries`);
      return data.data as WebhookDelivery[];
    },
    enabled: !!tenantId && !!webhookId,
    staleTime: 60_000,
  });
}

export function useUpdateWebhook() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      id: string;
      name?: string;
      url?: string;
      secret?: string;
      event_types?: string[];
      is_active?: boolean;
    }) => {
      const { id, ...rest } = body;
      const { data } = await apiClient.patch(`/webhooks/${id}`, rest);
      return data.data as WebhookEndpoint;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhooks', tenantId] }),
  });
}

export function useDeleteWebhook() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/webhooks/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhooks', tenantId] }),
  });
}

export function useCreateWebhook() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      name: string;
      url: string;
      eventTypes: string[];
      secret?: string;
      retryCount?: number;
      timeoutMs?: number;
    }) => {
      const { data } = await apiClient.post('/webhooks', {
        name: body.name,
        url: body.url,
        event_types: body.eventTypes,
        secret: body.secret,
        retry_count: body.retryCount,
        timeout_seconds: body.timeoutMs != null ? Math.round(body.timeoutMs / 1000) : undefined,
      });
      return data.data as WebhookEndpoint;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhooks', tenantId] }),
  });
}

export function useTestWebhook() {
  return useMutation({
    mutationFn: async (webhookId: string) => {
      const { data } = await apiClient.post(`/webhooks/${webhookId}/test`);
      return data.data;
    },
  });
}

export interface TenantSettings {
  timezone: string;
  session_timeout_minutes: number;
  require_reauth_for_sensitive_actions: boolean;
}

export function useTenantSettings() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['tenant-settings', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: TenantSettings }>('/settings/tenant');
      return data.data;
    },
    enabled: !!tenantId,
    staleTime: 10 * 60_000,
  });
}

export function useUpdateTenantSettings() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Partial<TenantSettings>) => {
      const { data } = await apiClient.patch<{ data: TenantSettings }>('/settings/tenant', body);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-settings', tenantId] }),
  });
}

export interface AuditLogEntry {
  id: string;
  created_at: string;
  user_id: string | null;
  user_email: string | null;
  user_display_name: string | null;
  event_category: string;
  event_type: string;
  action: string;
  outcome: string;
  resource_type: string | null;
  resource_name: string | null;
  ip_address: string | null;
  event_severity: string;
}

export interface AuditLogMeta {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export function useAuditLogs(params: { period?: string; category?: string; page?: number }) {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['audit-logs', tenantId, params],
    queryFn: async () => {
      const search = new URLSearchParams();
      if (params.period)   search.set('period',   params.period);
      if (params.category) search.set('category', params.category);
      if (params.page)     search.set('page',     String(params.page));
      const { data } = await apiClient.get<{ data: AuditLogEntry[]; meta: AuditLogMeta }>(
        `/audit-logs?${search.toString()}`,
      );
      return data;
    },
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

export function useBillingInfo() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['billing', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/settings/billing');
      return data.data as BillingInfo;
    },
    enabled: !!tenantId,
    staleTime: 10 * 60_000,
  });
}

export interface BrandingInfo {
  hasLogo: boolean;
  logoUrl: string | null;
  filename: string | null;
  size: number | null;
  mimeType: string | null;
  businessName: string | null;
  updatedAt: string | null;
}

export function useBranding() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['branding', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: BrandingInfo }>('/settings/branding');
      return data.data;
    },
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
  });
}

export function useUploadLogo() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('logo', file);
      const { data } = await apiClient.post<{ data: Partial<BrandingInfo> }>('/settings/branding/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['branding', tenantId] }),
  });
}

export function useDeleteLogo() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete('/settings/branding/logo');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['branding', tenantId] }),
  });
}

// ── Compliance Notification Settings (Sprint D.3) ─────────────────────────────

export interface ComplianceNotificationSettings {
  timezone: string;
  business_days: number[];
  daily_digest_send_hour: number;
  reminder_cadence_days: number;
  compliance_notifications_enabled: boolean;
}

export function useComplianceNotificationSettings() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['compliance-notification-settings', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: ComplianceNotificationSettings }>(
        '/tenant/notification-settings',
      );
      return data.data;
    },
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
  });
}

export function useUpdateComplianceNotificationSettings() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Partial<ComplianceNotificationSettings>) => {
      const { data } = await apiClient.patch<{ data: ComplianceNotificationSettings }>(
        '/tenant/notification-settings',
        body,
      );
      return data.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['compliance-notification-settings', tenantId] }),
  });
}

// ── Account Hygiene Settings (Sprint I) ───────────────────────────────────────

export interface AccountHygieneSettings {
  hygiene_scan_enabled: boolean;
  tenant_user_inactive_threshold_days: number;
  pending_invitation_expiry_days: number;
}

export function useAccountHygieneSettings() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['account-hygiene-settings', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: AccountHygieneSettings }>(
        '/tenant/account-hygiene-settings',
      );
      return data.data;
    },
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
  });
}

export function useUpdateAccountHygieneSettings() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Partial<AccountHygieneSettings>) => {
      const { data } = await apiClient.patch<{ data: AccountHygieneSettings }>(
        '/tenant/account-hygiene-settings',
        body,
      );
      return data.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['account-hygiene-settings', tenantId] }),
  });
}

export function useUpdateBusinessName() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (businessName: string) => {
      const { data } = await apiClient.patch('/settings/tenant', { businessName });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['branding', tenantId] }),
  });
}
